import { useRef, useEffect, useState, useCallback } from 'preact/hooks';
import { SokobanRenderer, THEMES } from '../game/renderer';
import { TouchController } from '../game/controls';
import { SokobanEngine, Direction } from '../game/engine';
import { getLevelById } from '../game/levels';
import { ACHIEVEMENTS } from '../game/achievements';
import { GameStateHook } from '../utils/useGameState';
import { api } from '../utils/api';

interface Props {
  levelId: number;
  isDaily?: boolean;
  onBack: () => void;
  onNextLevel: () => void;
  onMenu: () => void;
  gameState: GameStateHook;
}

export function GamePlay({ levelId, isDaily, onBack, onNextLevel, onMenu, gameState }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<SokobanEngine | null>(null);
  const rendererRef = useRef<SokobanRenderer | null>(null);
  const controllerRef = useRef<TouchController | null>(null);
  const [moves, setMoves] = useState(0);
  const [pushes, setPushes] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [stars, setStars] = useState(0);
  const [achievementToast, setAchievementToast] = useState<string | null>(null);
  const startTimeRef = useRef(Date.now());
  const completedRef = useRef(false);

  const renderGame = useCallback(() => {
    const engine = engineRef.current;
    const renderer = rendererRef.current;
    if (!engine || !renderer) return;
    renderer.render(engine.grid, engine.playerPos);
  }, []);

  // Store props in refs so the direction handler stays stable across renders
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;
  const levelIdRef = useRef(levelId);
  levelIdRef.current = levelId;
  const isDailyRef = useRef(isDaily);
  isDailyRef.current = isDaily;

  // Direction handler accessed via ref — stable identity, never triggers useEffect re-runs
  const handleDirectionRef = useRef<(direction: Direction) => void>(() => {});
  handleDirectionRef.current = (direction: Direction) => {
    const engine = engineRef.current;
    const renderer = rendererRef.current;
    if (!engine || !renderer || completedRef.current) return;
    const result = engine.move(direction);
    if (result.success) {
      setMoves(engine.moves);
      setPushes(engine.pushes);
      renderGame();

      // Haptic feedback
      const tg = (window as any).Telegram?.WebApp;
      if (result.pushed) {
        tg?.HapticFeedback?.impactOccurred('light');
      }

      if (result.completed) {
        const rating = engine.getStarRating();
        setStars(rating);
        setCompleted(true);
        completedRef.current = true;
        renderer.startCelebration();
        tg?.HapticFeedback?.notificationOccurred('success');

        if (isDailyRef.current) {
          const timeSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
          api.submitDailyScore(engine.moves, timeSeconds).catch(() => {});
        } else {
          gameStateRef.current.saveProgress(levelIdRef.current, engine.moves, rating).then(newAchievements => {
            if (newAchievements.length > 0) {
              const def = ACHIEVEMENTS.find(a => a.id === newAchievements[0]);
              if (def) {
                setAchievementToast(def.name);
                setTimeout(() => setAchievementToast(null), 3000);
              }
            }
          });
        }

        const animateParticles = () => {
          renderer.render(engine.grid, engine.playerPos);
          if (renderer['completionParticles']?.length > 0) {
            requestAnimationFrame(animateParticles);
          }
        };
        requestAnimationFrame(animateParticles);
      }
    }
  };

  // Stable wrapper that D-pad buttons and TouchController both call
  const handleDirection = useCallback((direction: Direction) => {
    handleDirectionRef.current(direction);
  }, []);

  // Initialize game
  useEffect(() => {
    setMoves(0);
    setPushes(0);
    setCompleted(false);
    setStars(0);
    setAchievementToast(null);
    startTimeRef.current = Date.now();
    completedRef.current = false;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const level = getLevelById(levelId);
    if (!level) return;

    const engine = new SokobanEngine(level.grid, level.optimalMoves);
    engine.maxUndos = Infinity;
    engineRef.current = engine;

    const renderer = new SokobanRenderer(canvas, THEMES.warehouse);
    rendererRef.current = renderer;

    const controller = new TouchController(canvas, handleDirection);
    controllerRef.current = controller;

    // Handle undo/reset keyboard shortcuts
    const handleUndo = () => {
      if (engine.undo()) {
        setMoves(engine.moves);
        setPushes(engine.pushes);
        renderGame();
      }
    };
    const handleReset = () => {
      engine.reset();
      setMoves(0);
      setPushes(0);
      setCompleted(false);
      completedRef.current = false;
      setStars(0);
      renderGame();
    };
    document.addEventListener('sokoban:undo', handleUndo);
    document.addEventListener('sokoban:reset', handleReset);

    // Handle resize
    const handleResize = () => {
      renderer.resize();
      renderGame();
    };
    window.addEventListener('resize', handleResize);

    // Initial render
    renderGame();

    return () => {
      controller.destroy();
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('sokoban:undo', handleUndo);
      document.removeEventListener('sokoban:reset', handleReset);
    };
  }, [levelId]);

  const handleUndo = () => {
    const engine = engineRef.current;
    if (!engine || completed) return;
    if (engine.undo()) {
      setMoves(engine.moves);
      setPushes(engine.pushes);
      renderGame();
    }
  };

  const handleReset = () => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.reset();
    setMoves(0);
    setPushes(0);
    setCompleted(false);
    completedRef.current = false;
    setStars(0);
    startTimeRef.current = Date.now();
    renderGame();
  };

  const handleShare = async () => {
    const level = getLevelById(levelId);
    const label = isDaily ? 'Daily Puzzle' : `Sokoban Level ${levelId}`;
    const text = [
      label,
      `${'★'.repeat(stars)}${'☆'.repeat(3 - stars)} | ${moves}${level ? ` / ${level.optimalMoves}` : ''} moves`,
      `Play: t.me/sokoban_pushbox_classic_bot`,
    ].join('\n');

    const tg = (window as any).Telegram?.WebApp;

    // Try native share first (works well on mobile)
    if (navigator.share) {
      try {
        await navigator.share({ text });
        return;
      } catch { /* user cancelled or not supported */ }
    }

    // Try Telegram's inline sharing
    if (tg?.switchInlineQuery) {
      try {
        tg.switchInlineQuery(text, ['users']);
        return;
      } catch { /* not supported */ }
    }

    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(text);
      tg?.showAlert?.('Copied to clipboard!') ?? alert('Copied to clipboard!');
    } catch {
      tg?.showAlert?.(text) ?? alert(text);
    }
  };

  const level = getLevelById(levelId);

  return (
    <div style={styles.container}>
      {/* Top HUD */}
      <div style={styles.hud}>
        <button style={styles.hudBtn} onClick={onBack}>Back</button>
        <div style={styles.hudCenter}>
          <span style={styles.levelLabel}>{isDaily ? 'Daily Puzzle' : `Level ${levelId}`}</span>
          {level && !isDaily && <span style={styles.worldLabel}>{level.title}</span>}
        </div>
        <button style={styles.hudBtn} onClick={handleReset}>
          Restart
        </button>
      </div>

      {/* Stats bar */}
      <div style={styles.statsBar}>
        <span style={styles.statItem}>Moves: {moves}{level ? ` / ${level.optimalMoves}` : ''}</span>
        <span style={styles.statItem}>Pushes: {pushes}</span>
      </div>

      {/* Game canvas */}
      <canvas ref={canvasRef} style={styles.canvas} />

      {/* Bottom controls: Reset (left) | D-pad (center) | Undo (right) */}
      <div style={styles.controls}>
        <button style={styles.controlBtn} onClick={handleReset}>
          Reset
        </button>

        <div style={styles.dpad}>
          <div style={styles.dpadRow}>
            <div style={styles.dpadSpacer} />
            <button
              style={styles.dpadBtn}
              onClick={() => handleDirection('up')}
              disabled={completed}
            >
              ▲
            </button>
            <div style={styles.dpadSpacer} />
          </div>
          <div style={styles.dpadRow}>
            <button
              style={styles.dpadBtn}
              onClick={() => handleDirection('left')}
              disabled={completed}
            >
              ◀
            </button>
            <button
              style={styles.dpadBtn}
              onClick={() => handleDirection('down')}
              disabled={completed}
            >
              ▼
            </button>
            <button
              style={styles.dpadBtn}
              onClick={() => handleDirection('right')}
              disabled={completed}
            >
              ▶
            </button>
          </div>
        </div>

        <button style={styles.controlBtn} onClick={handleUndo} disabled={completed}>
          Undo
        </button>
      </div>

      {/* Level complete overlay */}
      {completed && (
        <div style={styles.overlay}>
          <div style={styles.completeCard}>
            <h2 style={styles.completeTitle}>Level Complete!</h2>
            <div style={styles.starsDisplay}>
              {[1, 2, 3].map(s => (
                <span key={s} style={{
                  ...styles.completeStar,
                  opacity: s <= stars ? 1 : 0.2,
                  transform: s <= stars ? 'scale(1)' : 'scale(0.7)',
                }}>
                  {s <= stars ? '★' : '☆'}
                </span>
              ))}
            </div>
            <p style={styles.completeStats}>
              {moves}{level ? ` / ${level.optimalMoves}` : ''} moves
            </p>
            <div style={styles.completeButtons}>
              {!isDaily && (
                <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={onNextLevel}>
                  Next Level
                </button>
              )}
              <button style={{ ...styles.btn, ...styles.btnOutline }} onClick={handleReset}>
                Retry for 3 Stars
              </button>
              <button style={{ ...styles.btn, ...styles.btnOutline }} onClick={handleShare}>
                Share Result
              </button>
              <button style={styles.btnLink} onClick={onMenu}>
                Back to Menu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Achievement toast */}
      {achievementToast && (
        <div style={styles.achievementToast}>
          Achievement Unlocked: {achievementToast}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, any> = {
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  hud: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
  },
  hudBtn: {
    background: 'none',
    border: 'none',
    color: '#4ecdc4',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: '8px',
  },
  hudCenter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  levelLabel: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#eee',
  },
  worldLabel: {
    fontSize: '11px',
    color: '#888',
  },
  statsBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    padding: '4px 16px 8px',
  },
  statItem: {
    fontSize: '12px',
    color: '#aaa',
  },
  canvas: {
    flex: 1,
    width: '100%',
    display: 'block',
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    padding: '8px 12px 16px',
  },
  controlBtn: {
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    color: '#eee',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    touchAction: 'manipulation',
  },
  // D-Pad
  dpad: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
  dpadRow: {
    display: 'flex',
    gap: '2px',
  },
  dpadBtn: {
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '10px',
    color: '#eee',
    fontSize: '24px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    touchAction: 'manipulation',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  },
  dpadSpacer: {
    width: '64px',
    height: '64px',
  },
  dpadCenter: {
    width: '64px',
    height: '64px',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  completeCard: {
    background: '#2a2a4a',
    borderRadius: '20px',
    padding: '32px 24px',
    textAlign: 'center',
    maxWidth: '300px',
    width: '90%',
  },
  completeTitle: {
    margin: '0 0 16px',
    fontSize: '24px',
    color: '#4ecdc4',
  },
  starsDisplay: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  completeStar: {
    fontSize: '36px',
    color: '#feca57',
    transition: 'all 0.3s',
  },
  completeStats: {
    color: '#aaa',
    fontSize: '14px',
    marginBottom: '20px',
  },
  completeButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  btn: {
    border: 'none',
    borderRadius: '10px',
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnPrimary: {
    background: '#4ecdc4',
    color: '#1a1a2e',
  },
  btnOutline: {
    background: 'transparent',
    color: '#eee',
    border: '1px solid rgba(255,255,255,0.2)',
  },
  btnLink: {
    background: 'none',
    border: 'none',
    color: '#888',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: '8px',
  },
  achievementToast: {
    position: 'absolute',
    top: '8px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(78, 205, 196, 0.95)',
    color: '#1a1a2e',
    padding: '10px 20px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    zIndex: 200,
    whiteSpace: 'nowrap',
  },
};
